import { useEffect, useState, useMemo, useCallback } from 'react'
import { Grid } from '@mui/material'
import { useFormikContext } from 'formik'
import { t } from 'i18next'

import Dialog from 'src/components/shared/ui/Dialog/Dialog'
import { SearchIcon } from 'src/components/shared/ui/Icons'
import { AutoCompleteField } from 'src/components/shared/ui/form/fields'
import FieldsetGroup from 'src/components/shared/ui/form/fields/FieldsetGroup/FieldsetGroup'
import InputField from 'src/components/shared/ui/form/fields/InputField'
import LexnetIconButton from 'src/components/shared/ui/form/fields/LexnetIconButton'
import OrigenForm from 'src/components/views/EscritosLexnet/components/IniciadorAsunto/components/Origen/components/forms/OrigenForm'
import { TIPO_ORIGEN } from 'src/components/views/EscritosLexnet/components/IniciadorAsunto/components/Origen/consts'
import RemoveInfoButton from 'src/components/views/EscritosLexnet/components/shared/components/RemoveInfoButton'
import NumeroCompuestoField from 'src/components/views/EscritosLexnet/components/shared/components/fields/NumeroCompuesto/NumeroCompuestoField'
import useClearFormSections from 'src/components/views/EscritosLexnet/components/shared/hooks/useClearFormSections'
import { EscritosFormValues } from 'src/components/views/EscritosLexnet/components/shared/types/escritosFormTypes'
import {
  OrganoOrigenFormInitialvalues,
  OrganoOrigenFormValidations,
  OrganoOrigenFormValues
} from 'src/components/views/EscritosLexnet/components/shared/types/organoOrigenTypes'
import { CautelarOpciones } from 'src/enums/EscritosLegales'
import { FormField } from 'src/enums/Form'
import useFetching from 'src/hooks/useFetching'
import useNewModal from 'src/hooks/useModal'
import { getOrganismosExtrajudiciales } from 'src/services/CatalogService'

const styles = {
  '& :not(.disabled) .MuiInputBase-input, & :not(.disabled) .MuiInputBase-root': {
    cursor: 'pointer'
  }
}

function OrganoOrigen(): JSX.Element {
  const [disabledRemoveButton, setDisabledRemoveButton] = useState(true)
  const { values, setFieldValue } = useFormikContext<EscritosFormValues>()
  const {
    isOpened: isOrganoModalOpened,
    close: closeOrganoModal,
    open: openOrganoModal
  } = useNewModal()

  const { clearOrganoOrigenFieldset, clearPartialProcedimientoSection } = useClearFormSections()

  const organoOrigen = values[FormField.ORGANO_ORIGEN]
  const procedimiento = values[FormField.PROCEDIMIENTO]
  const datosCautelar = values[FormField.CAUTELAR]
  const organoOrigenOrganismoOrigen = values[FormField.ORGANO_ORIGEN_ORGANISMO_ORIGEN]
  const organoOrigenNumeroExpediente = values[FormField.ORGANO_ORIGEN_NUMERO_EXPEDIENTE]
  const organoOrigenYearExpediente = values[FormField.ORGANO_ORIGEN_YEAR_EXPEDIENTE]
  const organoOrigenTipoOrigen = values[FormField.ORGANO_ORIGEN_TIPO_ORIGEN]
  const showTribunalSupremoFields =
    values[FormField.ORGANO_DESTINO]?.organoDestinoOrganoJudicial?.tribunalSupremo

  const [
    executeOrganismosExtrajudicialesQueryFlag,
    setExecuteOrganismosExtrajudicialesQueryFlag
  ] = useState<boolean>(true)

  const organismosExtrajudiciales = useFetching({
    key: [FormField.ORGANO_ORIGEN_ORGANISMO_ORIGEN],
    fetchFn: getOrganismosExtrajudiciales,
    enabled: false
  })

  useEffect(() => {
    if (showTribunalSupremoFields && executeOrganismosExtrajudicialesQueryFlag) {
      organismosExtrajudiciales.refetch()
      setExecuteOrganismosExtrajudicialesQueryFlag(false)
    }
  }, [
    showTribunalSupremoFields,
    organismosExtrajudiciales,
    executeOrganismosExtrajudicialesQueryFlag
  ])

  useEffect(() => {
    if (organoOrigen) {
      if (procedimiento) {
        clearPartialProcedimientoSection(values)
      }
    }
    // eslint-disable-next-line
  }, [organoOrigen])

  const clearOrgano = useCallback(() => {
    clearOrganoOrigenFieldset()
    clearPartialProcedimientoSection(values)
    setDisabledRemoveButton(true)

    // eslint-disable-next-line
  }, [values])

  function getButtons() {
    return (
      <>
        {(!organoOrigenTipoOrigen || organoOrigenTipoOrigen === TIPO_ORIGEN.JUDICIAL) && (
          <LexnetIconButton
            title={t('tooltips.buscar_organo_origen')}
            handleClick={openOrganoModal}
          >
            <SearchIcon />
          </LexnetIconButton>
        )}
        <RemoveInfoButton
          disabled={disabledRemoveButton}
          clear={clearOrgano}
          firstDialogMessage={t('dialog.message.remove_organo_origen')}
        />
      </>
    )
  }

  function formSubmit(values: OrganoOrigenFormValues) {
    setFieldValue(FormField.ORGANO_ORIGEN, values, false)
    setFieldValue(
      FormField.ORGANO_ORIGEN_DESCRIPCION,
      `${values[FormField.ORGANO_ORIGEN_ORGANO_JUDICIAL]?.nombre} DE ${values[
        FormField.ORGANO_ORIGEN_PARTIDO_JUDICIAL
      ]?.nombreMunicipio.toUpperCase()}`,
      false
    )
    setFieldValue(
      FormField.ORGANO_ORIGEN_ORDEN_DESCRIPCION,
      values[FormField.ORGANO_ORIGEN_ORDEN_JUDICIAL]?.descripcion
    )
    setDisabledRemoveButton(false)
    closeOrganoModal()
  }

  function requiredField() {
    return datosCautelar === CautelarOpciones.SI
  }

  useEffect(() => {
    if (organoOrigenOrganismoOrigen || organoOrigenNumeroExpediente || organoOrigenYearExpediente) {
      setDisabledRemoveButton(false)
    }
  }, [organoOrigenOrganismoOrigen, organoOrigenNumeroExpediente, organoOrigenYearExpediente])

  useEffect(() => {
    if (!organoOrigen) {
      setDisabledRemoveButton(true)
    }
  }, [organoOrigen])

  const renderComponents = useMemo((): JSX.Element => {
    return showTribunalSupremoFields && organoOrigenTipoOrigen === TIPO_ORIGEN.EXTRAJUDICIAL ? (
      <Grid container spacing={2}>
        <Grid item xs={6} sm={4}>
          <AutoCompleteField
            id={FormField.ORGANO_ORIGEN_ORGANISMO_ORIGEN}
            items={organismosExtrajudiciales?.data?.results || []}
            label={t('form.field.datos_organismo_origen')}
            itemName="descripcion"
            required
          />
        </Grid>
        <Grid item xs={6} sm={4}>
          <NumeroCompuestoField
            label={t('form.field.numero_expediente')}
            ids={{
              number: FormField.ORGANO_ORIGEN_NUMERO_EXPEDIENTE,
              year: FormField.ORGANO_ORIGEN_YEAR_EXPEDIENTE
            }}
            required
          />
        </Grid>
      </Grid>
    ) : (
      <Grid container spacing={2}>
        <Grid item xs={6} sm={4} onClick={openOrganoModal}>
          <InputField
            id={FormField.ORGANO_ORIGEN_DESCRIPCION}
            label={t('form.field.organo')}
            readOnly
            required={requiredField()}
            sx={styles}
          />
        </Grid>
        <Grid item xs={6} sm={4} onClick={openOrganoModal}>
          <InputField
            id={FormField.ORGANO_ORIGEN_ORDEN_DESCRIPCION}
            label={t('form.field.orden')}
            readOnly
            required={requiredField()}
            sx={styles}
          />
        </Grid>
      </Grid>
    )
    // eslint-disable-next-line
  }, [showTribunalSupremoFields, organismosExtrajudiciales])

  const [legend, setLegend] = useState('')

  useEffect(() => {
    if (organoOrigenTipoOrigen === TIPO_ORIGEN.EXTRAJUDICIAL) {
      setLegend(t('form.field.datos_organismo_origen'))
    } else {
      setLegend(t('form.legend.organo_origen'))
    }
  }, [organoOrigenTipoOrigen])

  return (
    <>
      <FieldsetGroup legend={legend} buttons={getButtons()} isSecondaryType>
        {renderComponents}
      </FieldsetGroup>
      <Dialog
        title={t('form.dialog.title.organo_origen')}
        isOpen={isOrganoModalOpened}
        close={closeOrganoModal}
        cancel={() => ''}
        withForm
        validateFormik={false}
        formInitialValues={values[FormField.ORGANO_ORIGEN] || OrganoOrigenFormInitialvalues}
        formValidationScheme={OrganoOrigenFormValidations}
        formHandleSubmit={formSubmit}
      >
        <OrigenForm />
      </Dialog>
    </>
  )
}

export default OrganoOrigen
